import type { AvailabilityLightInput, AvailabilityLightView, LightColor } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { errorMessage, get, post } from "../api";
import { useSession } from "../session";
import { Alert, cx } from "./ui";

/** A row in the side menu (the light and the Qualiphy switch share it). */
export const MENU_ROW = "flex w-full items-center gap-2.5 rounded-[var(--radius-control)] px-3 py-2 text-[13px] font-medium text-white/80 hover:bg-white/5 hover:text-white";

const DOT: Record<LightColor, string> = { green: "bg-[#22c55e]", yellow: "bg-[#facc15]", red: "bg-[#ef4444]" };

const describe = (v: Pick<AvailabilityLightView, "color" | "delayMinutes" | "noNurseUntil" | "atEvent">) =>
  v.atEvent ? "No nurse at the clinic" : v.noNurseUntil !== undefined ? "No nurse on shift" : v.color === "green" ? "Booking now: no delay" : v.color === "yellow" ? `Booking delay: ${v.delayMinutes} min` : "No online bookings";

/** "2:15 PM" today, "Oct 19, 9:00 AM" another day. */
const time = (iso: string) => {
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  return new Intl.DateTimeFormat("en-US", sameDay ? { timeStyle: "short" } : { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(d);
};

/**
 * The availability light, in the menu under the logo: how soon online booking offers clinic visits
 * now (red on its own while no nurse is on shift). A nurse on shift, managers, and admins change it from its menu; a change lasts until
 * someone changes it or the day ends, then it's back to normal (the delay in Settings → Booking).
 */
export function AvailabilityLight() {
  const { can } = useSession();
  const qc = useQueryClient();
  const allowed = can("appointments.manage", "charts.write", "availability.manage", "settings.manage");
  const q = useQuery({ queryKey: ["availability-light"], queryFn: () => get<AvailabilityLightView>("/api/availability-light"), enabled: allowed, refetchInterval: 30_000 });
  const set = useMutation({
    mutationFn: (v: AvailabilityLightInput) => post<AvailabilityLightView>("/api/availability-light", v),
    onSuccess: (v) => {
      qc.setQueryData(["availability-light"], v);
      setOpen(false);
    },
  });
  const [open, setOpen] = useState(false);
  const [minutes, setMinutes] = useState("");
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => !box.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);
  const v = q.data;
  if (!allowed || !v) return null;
  const atEvent = v.atEvent ? `At ${v.atEvent.names.join(" and ")} until ${time(v.atEvent.until)}` : null;
  const changed = atEvent && !v.held
    ? atEvent
    : v.held && v.change
    ? `${v.change.by} set it to ${v.held} ${time(v.change.at)}, but no nurse is ${v.atEvent ? "at the clinic" : "on shift"}, so nothing can be booked${v.atEvent ? ` until ${time(v.atEvent.until)}` : v.noNurseUntil ? ` until ${time(v.noNurseUntil)}` : ""}`
    : v.change
    ? `Set by ${v.change.by} ${time(v.change.at)}${v.change.until ? `, until changed (or the end of the day)` : ", until changed"}`
    : v.noNurseUntil !== undefined
      ? v.noNurseUntil
        ? `Until the next shift starts, ${time(v.noNurseUntil)}`
        : "No shifts in the next week"
      : "Normal";
  // Shown under the label: until when, if it isn't the normal state.
  const sub = atEvent && !v.held ? atEvent.replace(/^At/, "at") : v.held && v.change ? `${v.change.by}: ${v.held}` : v.noNurseUntil ? `until ${time(v.noNurseUntil)}` : v.change ? `set by ${v.change.by}` : null;
  return (
    <div ref={box} className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} className={MENU_ROW} title={`${describe(v)}. ${changed}.`} aria-expanded={open}>
        <span className={cx("size-2.5 shrink-0 rounded-full shadow-[0_0_0_3px_rgba(255,255,255,0.08)]", DOT[v.color])} aria-hidden />
        <span className="min-w-0 flex-1 text-left">
          <span className="block truncate">{describe(v)}</span>
          {sub && <span className="block truncate text-xs text-white/50">{sub}</span>}
        </span>
        <svg viewBox="0 0 20 20" className={cx("size-4 shrink-0 text-white/40 transition", open && "rotate-180")} fill="currentColor" aria-hidden>
          <path d="M5.5 7.5 10 12l4.5-4.5" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="absolute inset-x-0 top-full z-50 mt-1 rounded-[var(--radius-panel)] border border-line bg-white p-3 text-sm text-ink shadow-lg">
          <p className="text-xs text-muted">{changed}.</p>
          {v.canChange ? (
            <div className="mt-2 flex flex-col gap-1">
              <Choice color="green" label="Green: book now" detail="No delay until it's changed (or the end of the day)." busy={set.isPending} onPick={() => set.mutate({ color: "green", minutes: null })} />
              {v.normal === "yellow" && <Choice color="yellow" label={`Yellow: normal (${v.normalDelayMinutes} min)`} detail="Back to the usual delay." busy={set.isPending} onPick={() => set.mutate({ color: "normal", minutes: null })} />}
              <form
                className="flex items-center gap-2 rounded-[var(--radius-control)] px-2 py-1.5 hover:bg-mist"
                onSubmit={(e) => {
                  e.preventDefault();
                  const m = Number(minutes);
                  if (Number.isInteger(m) && m > 0) set.mutate({ color: "yellow", minutes: m });
                }}
              >
                <span className={cx("size-3 shrink-0 rounded-full", DOT.yellow)} aria-hidden />
                <span className="flex-1">Yellow, this time:</span>
                <input type="number" min={1} max={1440} inputMode="numeric" aria-label="Delay in minutes" className="w-16 rounded border border-line px-2 py-1" value={minutes} onChange={(e) => setMinutes(e.target.value)} placeholder="min" />
                <button type="submit" className="font-semibold text-brand" disabled={set.isPending}>
                  Set
                </button>
              </form>
              <Choice color="red" label="Red: no online bookings" detail="Until it's changed (or the end of the day)." busy={set.isPending} onPick={() => set.mutate({ color: "red", minutes: null })} />
              {v.normal === "green" && v.change && <Choice color="green" label="Back to normal" detail="No delay." busy={set.isPending} onPick={() => set.mutate({ color: "normal", minutes: null })} />}
            </div>
          ) : (
            <p className="mt-2 text-xs text-ink-soft">A nurse on shift, a manager, or an admin can change this.</p>
          )}
          {set.error && <Alert>{errorMessage(set.error)}</Alert>}
        </div>
      )}
    </div>
  );
}

function Choice({ color, label, detail, busy, onPick }: { color: LightColor; label: string; detail: string; busy: boolean; onPick: () => void }) {
  return (
    <button type="button" disabled={busy} onClick={onPick} className="flex items-start gap-2 rounded-[var(--radius-control)] px-2 py-1.5 text-left hover:bg-mist">
      <span className={cx("mt-1 size-3 shrink-0 rounded-full", DOT[color])} aria-hidden />
      <span>
        <span className="block font-semibold">{label}</span>
        <span className="block text-xs text-muted">{detail}</span>
      </span>
    </button>
  );
}
